import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Sparkles,
  Send,
  Check,
  X,
  AlertTriangle,
  Trash2,
  Star,
  RefreshCw,
  Inbox as InboxIcon,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORM_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
  google: "Google Reviews",
};

const PLATFORM_COLORS = {
  instagram: "#E1306C",
  facebook: "#1877F2",
  linkedin: "#0A66C2",
  whatsapp: "#25D366",
  google: "#FBBC05",
};

const STATUS_COLORS = {
  pending: "text-white/60",
  drafted: "text-orange-400",
  approved: "text-blue-400",
  sent: "text-green-400",
  escalated: "text-amber-400",
  dismissed: "text-white/30",
};

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draftAll, setDraftAll] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");

  const load = async () => {
    const [{ data: list }, { data: s }] = await Promise.all([
      api.get("/inbox"),
      api.get("/inbox/stats"),
    ]);
    setMessages(list);
    setStats(s);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return messages.filter((m) => {
      if (filterPlatform !== "all" && m.platform !== filterPlatform) return false;
      if (filterStatus !== "all" && m.status !== filterStatus) return false;
      return true;
    });
  }, [messages, filterPlatform, filterStatus]);

  const selected = useMemo(
    () => filtered.find((m) => m.id === selectedId) || filtered[0],
    [filtered, selectedId]
  );

  useEffect(() => {
    if (selected) setReplyDraft(selected.suggested_reply || "");
  }, [selected?.id, selected?.suggested_reply]);

  const seed = async () => {
    setBusy(true);
    try {
      await api.post("/inbox/seed");
      toast.success("Sample messages loaded");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const draftOne = async (mid) => {
    setBusy(true);
    try {
      await api.post(`/inbox/${mid}/draft`);
      toast.success("Draft generated");
      await load();
    } catch (e) {
      toast.error("Draft failed");
    } finally {
      setBusy(false);
    }
  };

  const draftPending = async () => {
    setDraftAll(true);
    try {
      const { data } = await api.post("/inbox/draft-pending");
      toast.success(`Drafted ${data.drafted} replies`);
      await load();
    } catch {
      toast.error("Failed");
    } finally {
      setDraftAll(false);
    }
  };

  const saveReply = async () => {
    if (!selected) return;
    await api.patch(`/inbox/${selected.id}/reply`, { reply_text: replyDraft });
    toast.success("Saved");
    await load();
  };

  const send = async () => {
    if (!selected) return;
    if (replyDraft !== selected.suggested_reply) {
      await api.patch(`/inbox/${selected.id}/reply`, { reply_text: replyDraft });
    }
    await api.post(`/inbox/${selected.id}/send`);
    toast.success("Sent (MOCKED)");
    await load();
  };

  const dismiss = async () => {
    if (!selected) return;
    await api.post(`/inbox/${selected.id}/dismiss`);
    toast.success("Dismissed");
    await load();
  };

  const escalate = async () => {
    if (!selected) return;
    await api.post(`/inbox/${selected.id}/escalate`);
    toast.success("Escalated to human");
    await load();
  };

  const del = async (mid) => {
    await api.delete(`/inbox/${mid}`);
    toast.success("Deleted");
    await load();
  };

  if (loading)
    return (
      <div className="space-y-4">
        <div className="h-10 w-72 bg-white/5 rounded animate-pulse" />
        <div className="h-96 bg-white/5 rounded-2xl animate-pulse" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="inbox-root">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">
            AI Reply Inbox
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">
            Reply to everyone, instantly.
          </h1>
          <p className="text-white/50 mt-2 max-w-2xl text-sm">
            Comments, reviews and DMs from every channel. AI drafts brand-voiced replies; sensitive
            messages are auto-escalated for human approval.
          </p>
        </div>
        <div className="flex gap-2">
          {messages.length === 0 && (
            <button
              onClick={seed}
              data-testid="inbox-seed-button"
              disabled={busy}
              className="glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 inline-flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" /> Load sample messages
            </button>
          )}
          <button
            onClick={draftPending}
            data-testid="inbox-draft-all"
            disabled={draftAll || !messages.some((m) => m.status === "pending")}
            className="brand-gradient px-4 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50 glow-orange"
          >
            <Sparkles className={`h-4 w-4 ${draftAll ? "animate-spin" : ""}`} />
            {draftAll ? "Drafting…" : "Draft all pending"}
          </button>
        </div>
      </header>

      {/* stat strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Pending" value={stats.pending} color="white" testid="inbox-stat-pending" />
          <Stat label="Drafted" value={stats.drafted} color="orange" testid="inbox-stat-drafted" />
          <Stat label="Sent" value={stats.sent} color="green" testid="inbox-stat-sent" />
          <Stat label="Escalated" value={stats.escalated} color="amber" testid="inbox-stat-escalated" />
          <Stat label="Total" value={stats.total} color="white" testid="inbox-stat-total" />
        </div>
      )}

      {messages.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <InboxIcon className="h-10 w-10 mx-auto text-white/30 mb-4" />
          <div className="text-white/50 max-w-md mx-auto">
            Your inbox is empty. Once channels are connected, comments, reviews and DMs flow in here automatically.
            Click <span className="text-white">Load sample messages</span> to see the AI in action.
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-4">
          {/* List */}
          <div className="lg:col-span-5 space-y-2">
            <div className="flex flex-wrap gap-2 mb-2">
              {["all", "pending", "drafted", "sent", "escalated"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  data-testid={`inbox-filter-status-${s}`}
                  className={`text-xs px-3 py-1.5 rounded-full uppercase tracking-[0.18em] ${
                    filterStatus === s ? "brand-gradient" : "glass hover:bg-white/5"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <PlatBtn val="all" active={filterPlatform} setVal={setFilterPlatform} label="All channels" />
              {Object.keys(PLATFORM_LABELS).map((p) => (
                <PlatBtn key={p} val={p} active={filterPlatform} setVal={setFilterPlatform} label={PLATFORM_LABELS[p]} color={PLATFORM_COLORS[p]} />
              ))}
            </div>

            <div className="space-y-2 max-h-[640px] overflow-y-auto pr-1">
              {filtered.length === 0 && (
                <div className="glass rounded-xl p-6 text-center text-sm text-white/50">No messages match.</div>
              )}
              {filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedId(m.id)}
                  data-testid={`inbox-item-${m.id}`}
                  className={`w-full text-left glass rounded-xl p-4 hover:bg-white/[0.04] transition border ${
                    selected?.id === m.id ? "border-orange-500/50" : "border-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: PLATFORM_COLORS[m.platform] || "#fff" }}
                      />
                      <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                        {PLATFORM_LABELS[m.platform]} · {m.kind}
                      </span>
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.2em] ${STATUS_COLORS[m.status]}`}>
                      {m.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{m.author}</span>
                    {m.author_handle && (
                      <span className="text-xs text-white/40 font-mono">{m.author_handle}</span>
                    )}
                    {m.rating != null && (
                      <span className="flex items-center gap-0.5 text-amber-400 text-xs">
                        {Array.from({ length: m.rating }).map((_, i) => (
                          <Star key={i} className="h-3 w-3 fill-current" />
                        ))}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/70 line-clamp-2">{m.text}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {selected ? (
              <div className="glass rounded-2xl p-6 lg:p-8 sticky top-6" data-testid="inbox-detail">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: PLATFORM_COLORS[selected.platform] }}
                    />
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50">
                      {PLATFORM_LABELS[selected.platform]} · {selected.kind}
                    </span>
                    {selected.sensitive && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 inline-flex items-center gap-1 uppercase tracking-[0.18em]">
                        <AlertTriangle className="h-3 w-3" /> Sensitive
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => del(selected.id)}
                    data-testid={`inbox-delete-${selected.id}`}
                    className="p-1.5 rounded-lg hover:bg-white/5"
                  >
                    <Trash2 className="h-4 w-4 text-white/40" />
                  </button>
                </div>

                {/* original */}
                <div className="border border-white/5 rounded-xl p-4 bg-white/[0.02]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{selected.author}</span>
                      {selected.author_handle && (
                        <span className="text-xs text-white/40 font-mono">{selected.author_handle}</span>
                      )}
                    </div>
                    {selected.rating != null && (
                      <span className="flex gap-0.5 text-amber-400">
                        {Array.from({ length: selected.rating }).map((_, i) => (
                          <Star key={i} className="h-3.5 w-3.5 fill-current" />
                        ))}
                      </span>
                    )}
                  </div>
                  <p className="text-white/85 leading-relaxed">{selected.text}</p>
                </div>

                {/* sensitive warning */}
                {selected.sensitive && selected.sensitive_reason && (
                  <div className="mt-4 border border-amber-500/30 bg-amber-500/[0.05] rounded-xl p-4 text-sm">
                    <div className="flex items-start gap-2 text-amber-400 font-semibold mb-1">
                      <AlertTriangle className="h-4 w-4 mt-0.5" /> Escalated for human review
                    </div>
                    <p className="text-white/70 text-xs leading-relaxed">{selected.sensitive_reason}</p>
                  </div>
                )}

                {/* draft */}
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-[0.25em] text-white/40">AI draft</div>
                    {!selected.suggested_reply && (
                      <button
                        onClick={() => draftOne(selected.id)}
                        data-testid={`inbox-draft-${selected.id}`}
                        disabled={busy}
                        className="brand-gradient px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 hover:opacity-90 disabled:opacity-50"
                      >
                        <Sparkles className="h-3 w-3" /> {busy ? "…" : "Draft reply"}
                      </button>
                    )}
                  </div>
                  <textarea
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    placeholder={selected.suggested_reply ? "" : "Click 'Draft reply' to let the AI write a brand-voiced response, or type your own here."}
                    rows={6}
                    data-testid="inbox-reply-textarea"
                    className="w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm leading-relaxed"
                  />
                  {selected.ai_notes && (
                    <div className="mt-2 text-xs text-white/40 italic">AI note: {selected.ai_notes}</div>
                  )}
                </div>

                {/* actions */}
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    onClick={send}
                    data-testid={`inbox-send-${selected.id}`}
                    disabled={!replyDraft || selected.status === "sent"}
                    className="brand-gradient px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90 glow-orange disabled:opacity-40"
                  >
                    <Send className="h-4 w-4" /> {selected.status === "sent" ? "Sent" : "Send reply (MOCKED)"}
                  </button>
                  <button
                    onClick={saveReply}
                    data-testid={`inbox-save-${selected.id}`}
                    disabled={!replyDraft}
                    className="glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 inline-flex items-center gap-2 disabled:opacity-40"
                  >
                    <Check className="h-4 w-4" /> Save draft
                  </button>
                  <button
                    onClick={escalate}
                    data-testid={`inbox-escalate-${selected.id}`}
                    className="glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 inline-flex items-center gap-2"
                  >
                    <AlertTriangle className="h-4 w-4" /> Escalate
                  </button>
                  <button
                    onClick={dismiss}
                    data-testid={`inbox-dismiss-${selected.id}`}
                    className="glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 inline-flex items-center gap-2"
                  >
                    <X className="h-4 w-4" /> Dismiss
                  </button>
                </div>

                <div className="mt-5 text-[10px] uppercase tracking-[0.2em] text-white/30 font-mono">
                  Received {new Date(selected.received_at).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="glass rounded-2xl p-10 text-center text-white/50">
                <MessageCircle className="h-8 w-8 mx-auto text-white/20 mb-3" />
                Select a message to reply.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  const colorClass = {
    orange: "text-orange-400",
    green: "text-green-400",
    amber: "text-amber-400",
    white: "text-white",
  }[color];
  return (
    <div className="glass rounded-xl p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
      <div className={`font-heading text-2xl font-black mt-1 ${colorClass}`}>{value}</div>
    </div>
  );
}

function PlatBtn({ val, active, setVal, label, color }) {
  return (
    <button
      onClick={() => setVal(val)}
      data-testid={`inbox-filter-platform-${val}`}
      className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${
        active === val ? "brand-gradient" : "glass hover:bg-white/5"
      }`}
    >
      {color && <span className="h-2 w-2 rounded-full" style={{ background: color }} />}
      {label}
    </button>
  );
}
